import React from "react";
import { Filter, Download } from "lucide-react";

/**
 * FilterRail — horizontal pill row for journey filtering.
 *
 * Replaces the old PNRFilters dialog *as the primary filter UI*. The
 * existing PNRFilters component is NOT deleted — it remains the
 * advanced-filters surface (date range, class, search, sort) reachable
 * via the "More filters" button. That keeps all of:
 *   filter-class / filter-clear / filter-date / filter-from-date /
 *   filter-search / filter-sort / filter-status / filter-to-date
 * working through the same component as before.
 *
 * Props:
 *   active        — pill id: "all" | "upcoming" | "live" | "confirmed"
 *                   | "rac" | "waiting" (defaults to "all")
 *   counts        — { total, upcoming, live, confirmed, rac, waiting }
 *   onFilterChange — (id) => void
 *   onMoreFilters — () => void
 *   onExport      — () => void
 *
 * New testid: filter-pill-{id}.
 */

const Pill = ({ id, label, count, dot, tone, active, onClick }) => {
  const toneText = {
    emerald: "text-emerald-400",
    amber: "text-amber-400",
    rose: "text-rose-400",
    sky: "text-sky-400",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`filter-pill-${id}`}
      className={`flex items-center gap-2 px-3 h-8 whitespace-nowrap rounded-lg text-[12px] font-medium transition-colors border ${
        active
          ? "bg-zinc-100 text-zinc-950 border-zinc-100"
          : "bg-zinc-900/60 text-zinc-300 hover:text-zinc-100 border-zinc-800 hover:border-zinc-700"
      }`}
    >
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />}
      <span>{label}</span>
      {count != null && (
        <span
          className={`text-[10.5px] font-mono ${
            active ? "text-zinc-600" : tone ? toneText[tone] : "text-zinc-500"
          }`}
        >
          {count}
        </span>
      )}
    </button>
  );
};

const FilterRail = ({
  active = "all",
  counts = {},
  onFilterChange,
  onMoreFilters,
  onExport,
}) => {
  const pills = [
    { id: "all",       label: "All Journeys", count: counts.total },
    { id: "upcoming",  label: "Upcoming",     count: counts.upcoming },
    {
      id: "live",
      label: "In Transit",
      count: counts.live,
      dot: "bg-sky-400 animate-pulse",
      tone: "sky",
    },
    { id: "confirmed", label: "Confirmed",    count: counts.confirmed, tone: "emerald" },
    { id: "rac",       label: "RAC",          count: counts.rac,       tone: "amber" },
    { id: "waiting",   label: "Waiting",      count: counts.waiting,   tone: "rose" },
  ];

  return (
    <div
      className="flex items-center gap-2 flex-wrap"
      data-testid="filter-rail"
    >
      {pills.map((p) => (
        <Pill
          key={p.id}
          {...p}
          active={p.id === active}
          onClick={() => onFilterChange?.(p.id)}
        />
      ))}
      <div className="flex-1" />
      <button
        type="button"
        onClick={onMoreFilters}
        data-testid="filter-rail-more"
        className="flex items-center gap-1.5 px-3 h-8 rounded-lg bg-zinc-900/60 border border-zinc-800 hover:border-zinc-700 text-zinc-400 hover:text-zinc-100 text-[12px] whitespace-nowrap transition-colors"
      >
        <Filter size={12} />
        More filters
      </button>
      <button
        type="button"
        onClick={onExport}
        data-testid="filter-rail-export"
        className="flex items-center gap-1.5 px-3 h-8 rounded-lg bg-zinc-900/60 border border-zinc-800 hover:border-zinc-700 text-zinc-400 hover:text-zinc-100 text-[12px] whitespace-nowrap transition-colors"
      >
        <Download size={12} />
        Export
      </button>
    </div>
  );
};

export default FilterRail;
